import React, { useState } from "react";
import { Alert, Badge, Button, Card, Group, Stack, Text } from "@mantine/core";
import { FiLock, FiTrash2 } from "react-icons/fi";
import type { IBlobMedia, IMediaAsset } from "@multiconsult-group/multiplacer-core";
import { BlobMediaPreview } from "../Releasenotes/BlobMedia";
import { getApiBaseUrl } from "../utils";

import { useDeleteMultiplacerMediaAssetsByKeyMutation, openapi } from "../../openapi";
import { useAppDispatch } from "../app/hooks";

export const MediaAssetCard: React.FC<{ asset: IMediaAsset }> = ({ asset }) => {
  const dispatch = useAppDispatch();

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const [deleteAsset, { isLoading: isDeleting }] = useDeleteMultiplacerMediaAssetsByKeyMutation();

  const isProtected = asset.isProtected === true;

  const previewMedia: IBlobMedia | null =
    asset.kind === "VIDEO"
      ? {
          kind: "video",
          blobPath: asset.blobPath,
          contentType: asset.mimeType ?? "video/mp4",
        }
      : asset.mimeType?.startsWith("image/")
        ? {
            kind: "image",
            blobPath: asset.blobPath,
            contentType: asset.mimeType,
          }
        : null;

  const handleReplace: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = "";
    if (!file) return;

    setError(undefined);
    setIsUploading(true);

    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", asset.kind);

      const res = await fetch(
        `${getApiBaseUrl()}/api/multiplacer/media-assets/${encodeURIComponent(asset.key)}/upload`,
        {
          method: "POST",
          body: form,
          credentials: "include",
        }
      );

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Upload failed (${res.status})`);
      }

      await res.json().catch(() => undefined);
      dispatch(openapi.util.invalidateTags(["MediaAssets"]));
    } catch (err: any) {
      setError(err?.message ?? "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (isProtected) return;

    setError(undefined);
    try {
      await deleteAsset({ key: asset.key } as any).unwrap();
      dispatch(openapi.util.invalidateTags(["MediaAssets"]));
    } catch (err: any) {
      setError(err?.data?.message ?? err?.message ?? "Delete failed");
    }
  };

  return (
    <Card withBorder radius="md" p="md" w={980} h={420} style={{ overflow: "hidden" }}>
      <Stack gap="xs" h="100%" style={{ overflow: "hidden" }}>
        <Group justify="space-between" align="start">
          <Stack gap={2}>
            <Text fw={600}>{asset.title}</Text>
            <Text size="sm" c="dimmed">
              {asset.key}
            </Text>
          </Stack>

          <Group gap="xs">
            <Badge variant="light">{asset.kind}</Badge>

            <Button
              size="xs"
              variant="light"
              component="label"
              loading={isUploading}
              disabled={isUploading}
            >
              Erstatt blob
              <input
                type="file"
                hidden
                accept={asset.kind === "VIDEO" ? "video/*" : "*"}
                onChange={handleReplace}
              />
            </Button>

            <Button
              size="xs"
              color="red"
              variant="light"
              leftSection={isProtected ? <FiLock /> : <FiTrash2 />}
              disabled={isProtected || isUploading || isDeleting}
              loading={isDeleting}
              onClick={handleDelete}
              title={isProtected ? "Denne er beskyttet og kan ikke slettes" : "Slett"}
            >
              Slett
            </Button>
          </Group>
        </Group>

        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        <Stack gap={2} style={{ overflow: "hidden" }}>
          <Text
            size="sm"
            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            <Text span c="dimmed">
              Blob:
            </Text>{" "}
            {asset.blobPath}
          </Text>

          {asset.version && (
            <Text size="sm">
              <Text span c="dimmed">
                Versjon:
              </Text>{" "}
              {asset.version}
            </Text>
          )}

          {asset.filename && (
            <Text
              size="sm"
              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              <Text span c="dimmed">
                Filnavn:
              </Text>{" "}
              {asset.filename}
            </Text>
          )}

          {typeof asset.sizeBytes === "number" && (
            <Text size="sm">
              <Text span c="dimmed">
                Størrelse:
              </Text>{" "}
              {Math.round((asset.sizeBytes ?? 0) / 1024 / 1024)} MB
            </Text>
          )}

          {asset.mimeType && (
            <Text size="sm">
              <Text span c="dimmed">
                MIME:
              </Text>{" "}
              {asset.mimeType}
            </Text>
          )}

          {asset.visibility && (
            <Text size="sm">
              <Text span c="dimmed">
                Tilgang:
              </Text>{" "}
              {asset.visibility}
            </Text>
          )}

          {(asset.tags?.length ?? 0) > 0 && (
            <Text
              size="sm"
              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              <Text span c="dimmed">
                Tags:
              </Text>{" "}
              {(asset.tags ?? []).join(", ")}
            </Text>
          )}

          {asset.description && (
            <Text size="sm" c="dimmed" lineClamp={2}>
              {asset.description}
            </Text>
          )}
        </Stack>

        {previewMedia && (
          <Stack gap={6} style={{ flex: 1, overflow: "hidden" }}>
            <Text size="sm" c="dimmed">
              Forhåndsvisning
            </Text>

            <div style={{ flex: 1, overflow: "hidden" }}>
              <BlobMediaPreview media={previewMedia} target="media-assets" />
            </div>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
